// pages/monitor/panels/peak/PeakDevice.js —— 削峰填谷 · 设备监控（依据《云平台单站控制（削峰填谷）需求文档》p4-11）
// 覆盖：实时运行 / PCS / BMS 总览 / BMS 电池簇 / 单体电池 / 电表 / 空调 / 消防安全 /
//       液冷机组 / DCDC / UPS / 光伏逆变器。本类型独有：DCDC、UPS、消防安全（IO / 传感器 / 测控）。
// 差异 #8：设备控制（PCS 开关机等）统一放在「控制中心」，本 tab 只做监控，不含控制按钮。
import React, { useMemo, useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import {
  Table, Tag, Select, Segmented, Space, Button, Modal, InputNumber, message,
} from 'antd';
import {
  ResponsiveContainer, ComposedChart, Line, Area, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { getDevices, getPowerCurve } from '../../../../config/stationData';
import { FIRE_IO, FIRE_MEASURE, DCDC_DEVICES } from './data';
import './peak.css';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const LEGEND = { wrapperStyle: { fontSize: 11, color: 'rgba(255,255,255,0.6)' }, iconSize: 8 };
const GRID = 'rgba(255,255,255,0.08)';
// 空调工作模式（削峰填谷文档 p8：支持显示 10 种模式）
const HVAC_MODES = ['制冷', '制热', '除湿', '送风', '待机', '加热除湿', '加湿', '制冷加湿', '加热加湿', '新风'];
// 削峰填谷站点数据中 has.pv=false，但设备监控文档/截图(p4)含「光伏逆变器」，此处提供本地回退数据以对齐图1导航
const PK_PV_FALLBACK = [
  { id: 'PV-01', name: '光伏逆变器 #1', running: true, state: '正常', dcPower: 52, acPower: 49, todayEnergy: 186, mppt: 4, dcVoltage: 620, efficiency: '97.4' },
  { id: 'PV-02', name: '光伏逆变器 #2', running: true, state: '正常', dcPower: 48, acPower: 45, todayEnergy: 164, mppt: 4, dcVoltage: 628, efficiency: '97.1' },
  { id: 'PV-03', name: '光伏逆变器 #3', running: true, state: '正常', dcPower: 44, acPower: 41, todayEnergy: 142, mppt: 4, dcVoltage: 636, efficiency: '96.8' },
];

const kvRows = (rows) => (
  <div className="dev-kv">
    {rows.map(([k, v]) => (
      <div className="dev-kv-row" key={k}><span className="dev-kv-k">{k}</span><span className="dev-kv-v">{v}</span></div>
    ))}
  </div>
);

const statusDot = (state) => (
  <span className="dev-status">
    <span className={`dot ${state === '正常' ? 'online' : 'fault'}`} />{state}
  </span>
);

// ---------------- 实时运行（对齐 p001 截图：6 KPI + 2×2 四图） ----------------
const RealtimeView = ({ dev, station }) => {
  const base = getPowerCurve(station.id);
  const pcsQ = dev.pcs.reduce((s, p) => s + p.q, 0);
  const pcsP = dev.pcs.reduce((s, p) => s + p.p, 0);
  const avgModule = dev.pcs.reduce((s, p) => s + p.moduleTemp, 0) / dev.pcs.length;
  const avgRackTemp = dev.racks.reduce((s, r) => s + r.temp, 0) / dev.racks.length;
  const rows = useMemo(() => base.map((r, i) => {
    const qStep = i < 24 ? pcsQ : i < 48 ? +(pcsQ * 0.85).toFixed(1) : i < 72 ? +(pcsQ * 1.1).toFixed(1) : +(pcsQ * 0.95).toFixed(1);
    const socDrift = Math.floor(i / 8) * 0.15;
    return {
      clock: r.clock,
      active: r.storage,
      reactive: qStep,
      r1: +(dev.racks[0].soc + socDrift).toFixed(1),
      r2: +(dev.racks[1].soc + socDrift * 0.9).toFixed(1),
      r3: +(dev.racks[2].soc + socDrift * 1.1).toFixed(1),
      r4: +(dev.racks[3].soc + socDrift * 0.8).toFixed(1),
      pcsTemp: +(avgModule + (i >= 32 && i < 80 ? 2.5 : 0) + (i % 16 === 0 ? 0.4 : 0)).toFixed(1),
      pcsYesterday: +(avgModule - 1.2 + (i >= 32 && i < 80 ? 2.0 : 0)).toFixed(1),
      stackTemp: +(avgRackTemp + (i >= 40 && i < 88 ? 1.2 : 0)).toFixed(1),
    };
  }), [base, dev, pcsQ, avgModule, avgRackTemp]);
  const totalCharge = dev.pcs.reduce((s, p) => s + p.chargeToday, 0);
  const totalDischarge = dev.pcs.reduce((s, p) => s + p.dischargeToday, 0);
  const avgSoc = dev.racks.reduce((s, r) => s + r.soc, 0) / dev.racks.length;
  const avgSoh = dev.racks.reduce((s, r) => s + r.soh, 0) / dev.racks.length;
  return (
    <div>
      <div className="kpi-grid pk-kpi6" style={{ marginBottom: 16 }}>
        <div className="kpi-card"><div className="kpi-label">储能实时有功</div><div className="kpi-value">{pcsP.toFixed(0)}<span className="unit">kW</span></div><div className="kpi-foot">储能侧（正充电 / 负放电）</div></div>
        <div className="kpi-card"><div className="kpi-label">储能实时无功</div><div className="kpi-value">{pcsQ.toFixed(1)}<span className="unit">kVar</span></div><div className="kpi-foot">全部 PCS 无功合计</div></div>
        <div className="kpi-card"><div className="kpi-label">电池堆SOC</div><div className="kpi-value">{avgSoc.toFixed(1)}<span className="unit">%</span></div><div className="kpi-foot">各电池堆 SOC 均值</div></div>
        <div className="kpi-card"><div className="kpi-label">电池堆SOH</div><div className="kpi-value">{avgSoh.toFixed(1)}<span className="unit">%</span></div><div className="kpi-foot">各电池堆 SOH 均值</div></div>
        <div className="kpi-card"><div className="kpi-label">今日充电量</div><div className="kpi-value">{totalCharge.toFixed(1)}<span className="unit">kWh</span></div><div className="kpi-foot">全部 PCS 充电合计</div></div>
        <div className="kpi-card"><div className="kpi-label">今日放电量</div><div className="kpi-value">{totalDischarge.toFixed(1)}<span className="unit">kWh</span></div><div className="kpi-foot">全部 PCS 放电合计</div></div>
      </div>
      <div className="grid-2">
        {/* 储能功率曲线：有功(kW)/无功(kVar) 柱状，双 Y 轴 */}
        <div className="panel">
          <div className="panel-title">储能功率曲线<small>有功 / 无功 · 单位 kW / kVar</small></div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={rows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="clock" interval={23} tick={TICK} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={44} />
              <YAxis yAxisId="r" orientation="right" tick={TICK} axisLine={false} tickLine={false} width={44} />
              <Tooltip contentStyle={TT} />
              <Legend {...LEGEND} />
              <Bar yAxisId="l" dataKey="active" name="有功功率 kW" fill="#f4c542" barSize={5} />
              <Bar yAxisId="r" dataKey="reactive" name="无功功率 kVar" fill="#38bdf8" barSize={5} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* PCS 平均温度曲线：昨日 / 实时 */}
        <div className="panel">
          <div className="panel-title">PCS 平均温度曲线<small>昨日平均 / 实时平均 · 单位 ℃</small></div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={rows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="clock" interval={23} tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} width={38} />
              <Tooltip contentStyle={TT} />
              <Legend {...LEGEND} />
              <Line type="linear" dataKey="pcsYesterday" name="昨日平均温度" stroke="#6b7280" dot={false} strokeWidth={2} strokeDasharray="5 4" />
              <Line type="linear" dataKey="pcsTemp" name="实时平均温度" stroke="#00d4aa" dot={false} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* 电池堆 SOC */}
        <div className="panel">
          <div className="panel-title">电池堆SOC<small>各电池堆 SOC · 单位 %</small></div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={rows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="pkSoc2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b7cf6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#8b7cf6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="clock" interval={23} tick={TICK} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={TICK} axisLine={false} tickLine={false} width={38} />
              <Tooltip contentStyle={TT} />
              <Legend {...LEGEND} />
              <Area type="linear" dataKey="r1" name="电池堆 #1" stroke="#8b7cf6" fill="url(#pkSoc2)" dot={false} strokeWidth={2} />
              <Line type="linear" dataKey="r2" name="电池堆 #2" stroke="#4dd8ff" dot={false} strokeWidth={2} />
              <Line type="linear" dataKey="r3" name="电池堆 #3" stroke="#52e8b0" dot={false} strokeWidth={2} />
              <Line type="linear" dataKey="r4" name="电池堆 #4" stroke="#f4c542" dot={false} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* 电堆平均温度 */}
        <div className="panel">
          <div className="panel-title">电堆平均温度<small>各电池堆平均温度 · 单位 ℃</small></div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={rows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="clock" interval={23} tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} width={38} />
              <Tooltip contentStyle={TT} />
              <Legend {...LEGEND} />
              <Line type="linear" dataKey="stackTemp" name="电堆平均温度" stroke="#f472b6" dot={false} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ---------------- PCS ----------------
const PcsView = ({ dev }) => (
  <div>
    <div className="dev-status-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 16 }}>
      {dev.pcs.map((p) => (
        <div className={`dev-card${p.alarm ? ' fault' : ''}`} key={p.id}>
          <div className="dev-card-head">
            <span className="dev-name">{p.name}</span>
            <span className="dev-status"><span className={`dot ${p.running ? 'online' : 'offline'}`} />{p.running ? p.grid : '停机'}</span>
          </div>
          {kvRows([
            ['有功功率', `${p.p} kW`],
            ['无功功率', `${p.q} kVar`],
            ['功率因数', p.pf],
            ['三相电压', `${p.uabc.join(' / ')} V`],
            ['三相电流', `${p.iabc.join(' / ')} A`],
            ['直流侧电压', `${p.branches[0].u} V`],
            ['模块温度', `${p.moduleTemp} ℃`],
            ['环境温度', `${p.envTemp} ℃`],
            ['频率', `${p.freq} Hz`],
            ['运行模式', p.mode],
          ])}
          <div className="panel-title" style={{ marginTop: 12, marginBottom: 6 }}>支路数据<small>直流侧</small></div>
          <Table
            size="small" pagination={false}
            rowKey="name"
            dataSource={p.branches}
            columns={[
              { title: '支路', dataIndex: 'name' },
              { title: '功率 (kW)', dataIndex: 'p' },
              { title: '电压 (V)', dataIndex: 'u' },
              { title: '电流 (A)', dataIndex: 'i' },
            ]}
          />
          {p.alarm ? <div className="peak-note" style={{ color: '#ff5a7f' }}>告警：{p.alarm}</div> : null}
        </div>
      ))}
    </div>
    <div className="peak-note">设备控制（PCS 开机 / 关机 / 并网 / 离网 / 故障复归）位于「控制中心 · 设备控制」，本页仅展示 PCS 数据。</div>
  </div>
);

// ---------------- BMS 总览 ----------------
const BmsView = ({ dev }) => (
  <div>
    <Table
      size="small" pagination={false} rowKey="id"
      dataSource={dev.racks}
      columns={[
        { title: '电池堆', dataIndex: 'name' },
        { title: '工作状态', dataIndex: 'state', render: (v) => <Tag color={v === '静置' ? 'default' : 'cyan'}>{v}</Tag> },
        { title: '总电压 (V)', dataIndex: 'voltage' },
        { title: '总电流 (A)', dataIndex: 'current' },
        { title: '温度 (℃)', dataIndex: 'temp' },
        { title: 'SOC (%)', dataIndex: 'soc' },
        { title: 'SOH (%)', dataIndex: 'soh' },
        { title: '可用电量 (kWh)', dataIndex: 'available' },
        { title: '电池簇数', dataIndex: 'cells', render: (v) => v / 26 },
        { title: '告警', dataIndex: 'alarm', render: (v) => (v ? <Tag color="orange">{v}</Tag> : '—') },
      ]}
    />
    <div className="peak-note">点击设备列表可切换查看对应设备（本页为全部电池堆总览）。</div>
  </div>
);

// ---------------- BMS 电池簇 ----------------
const ClusterView = ({ dev }) => {
  const [rack, setRack] = useState(dev.racks[0].id);
  const r = dev.racks.find((x) => x.id === rack) || dev.racks[0];
  const clusters = Array.from({ length: 4 }, (_, i) => ({
    key: `${r.id}-C${i + 1}`,
    name: `${i + 1} 号簇`,
    state: r.state === '静置' ? '静置' : i % 3 === 0 ? '放电' : '充电',
    contactorPos: r.contactorPos,
    contactorNeg: r.contactorNeg,
    voltage: +(r.voltage - i * 1.6).toFixed(1),
    current: +(r.current / 4 + (i - 1.5) * 4).toFixed(1),
    temp: +(r.temp + (i % 3) * 0.6).toFixed(1),
    soc: +(r.soc - i * 0.8).toFixed(1),
  }));
  return (
    <div>
      <div className="peak-toolbar">
        <span className="peak-toolbar-label">切换设备</span>
        <Select value={rack} onChange={setRack} style={{ width: 200 }}
          options={dev.racks.map((x) => ({ value: x.id, label: x.name }))} />
      </div>
      <Table
        size="small" pagination={false} rowKey="key"
        dataSource={clusters}
        columns={[
          { title: '电池簇', dataIndex: 'name' },
          { title: '工作状态', dataIndex: 'state', render: (v) => <Tag color={v === '静置' ? 'default' : v === '充电' ? 'cyan' : 'green'}>{v}</Tag> },
          { title: '主正接触器', dataIndex: 'contactorPos' },
          { title: '主负接触器', dataIndex: 'contactorNeg' },
          { title: '总电压 (V)', dataIndex: 'voltage' },
          { title: '总电流 (A)', dataIndex: 'current' },
          { title: '温度 (℃)', dataIndex: 'temp' },
          { title: 'SOC (%)', dataIndex: 'soc' },
        ]}
      />
      <div className="peak-note">主正 / 主负接触器状态可结合「控制中心 · 设备控制」执行上电（先合闸再开 PCS）与下电（先关 PCS 再分闸）。</div>
    </div>
  );
};

// ---------------- 单体电池 ----------------
const CellView = ({ dev }) => {
  const [rack, setRack] = useState(dev.racks[0].id);
  const [cluster, setCluster] = useState('C1');
  const [param, setParam] = useState('voltage');
  const [view, setView] = useState('dist');
  const [range, setRange] = useState('all');
  const [yZoom, setYZoom] = useState('auto');

  const cells = useMemo(() => {
    const list = dev.cells.map((c, i) => ({ ...c, idx: i + 1 }));
    const ci = Number(cluster.slice(1)) - 1;
    const sliced = list.slice(ci * 26, ci * 26 + 26);
    if (range === 'low') return sliced.slice(0, 13);
    if (range === 'high') return sliced.slice(13);
    return sliced;
  }, [dev.cells, cluster, range]);

  const meta = { voltage: { label: '电压', unit: 'V' }, temp: { label: '温度', unit: '℃' }, soc: { label: 'SOC', unit: '%' }, soh: { label: 'SOH', unit: '%' } }[param];
  const data = cells.map((c) => ({ no: c.no, value: c[param] }));
  const values = data.map((d) => d.value);
  // 柱状图 Y 轴缩放域（文档 p7：柱状图支持 X 轴和 Y 轴缩放）
  const yMin = Math.min(...values);
  const yMax = Math.max(...values);
  const ySpan = (yMax - yMin) || 1;
  const yDomain = yZoom === 'in' ? [yMin - ySpan * 0.05, yMax + ySpan * 0.05]
    : yZoom === 'out' ? [yMin - ySpan * 1, yMax + ySpan * 1]
      : [yMin - ySpan * 0.2, yMax + ySpan * 0.2];

  return (
    <div>
      <div className="peak-toolbar">
        <span className="peak-toolbar-label">切换设备</span>
        <Select value={rack} onChange={setRack} style={{ width: 160 }} options={dev.racks.map((x) => ({ value: x.id, label: x.name }))} />
        <Select value={cluster} onChange={setCluster} style={{ width: 120 }}
          options={['C1', 'C2', 'C3', 'C4'].map((c) => ({ value: c, label: `${c.slice(1)} 号簇` }))} />
        <span className="peak-toolbar-label">参数</span>
        <Segmented value={param} onChange={setParam}
          options={[{ label: '电压', value: 'voltage' }, { label: '温度', value: 'temp' }, { label: 'SOC', value: 'soc' }, { label: 'SOH', value: 'soh' }]} />
        <span className="peak-toolbar-label">视图</span>
        <Segmented value={view} onChange={setView}
          options={[{ label: '分布图', value: 'dist' }, { label: '表格', value: 'table' }, { label: '柱状图', value: 'bar' }]} />
      </div>

      {view === 'dist' ? (
        <ResponsiveContainer width="100%" height={360}>
          <ScatterChart margin={{ top: 10, right: 16, bottom: 10, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="no" name="单体编号" tick={TICK} axisLine={false} tickLine={false} domain={['dataMin', 'dataMax']} />
            <YAxis dataKey="value" name={meta.label} tick={TICK} axisLine={false} tickLine={false} width={52} domain={['dataMin - 2', 'dataMax + 2']} />
            <Tooltip contentStyle={TT} />
            <Scatter name={`单体${meta.label}（${meta.unit}）`} data={data} fill="#4dd8ff" />
          </ScatterChart>
        </ResponsiveContainer>
      ) : null}

      {view === 'bar' ? (
        <div>
          <div className="peak-toolbar">
            <span className="peak-toolbar-label">X 轴范围</span>
            <Segmented value={range} onChange={setRange}
              options={[{ label: '全部 26 只', value: 'all' }, { label: '1–13', value: 'low' }, { label: '14–26', value: 'high' }]} />
            <span className="peak-toolbar-label">Y 轴缩放</span>
            <Segmented value={yZoom} onChange={setYZoom}
              options={[{ label: '自动', value: 'auto' }, { label: '放大', value: 'in' }, { label: '缩小', value: 'out' }]} />
          </div>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={data} margin={{ top: 10, right: 16, bottom: 10, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="no" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} width={52} domain={yDomain} />
              <Tooltip contentStyle={TT} />
              <Bar dataKey="value" name={`${meta.label}（${meta.unit}）`} fill="#52e8b0" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {view === 'table' ? (
        <Table
          size="small" pagination={false} rowKey="no" scroll={{ y: 320 }}
          dataSource={data}
          columns={[
            { title: '单体编号', dataIndex: 'no', render: (v) => `${v} 号` },
            { title: `${meta.label}（${meta.unit}）`, dataIndex: 'value' },
          ]}
        />
      ) : null}
      <div className="peak-note">柱状图/分布图支持 X、Y 轴缩放；切换设备可查看不同电池堆/簇下的单体数据。</div>
    </div>
  );
};

// ---------------- 电表 / 空调 / 液冷 ----------------
const MeterView = ({ dev }) => {
  const update = '2026-10-12 10:31:20';
  // 分时电量占比（尖/峰/平/谷/深谷），削峰填谷站按电价时段配置估算
  const SLOT = { sharp: 0.052, peak: 0.287, flat: 0.088, valley: 0.532, deep: 0.040 };
  const f = (n, d = 1) => +n.toFixed(d);
  const H = { fontSize: 12, color: 'rgba(255,255,255,0.5)', padding: '6px 8px', borderBottom: '1px solid rgba(0,180,216,0.12)' };
  const K = { fontSize: 12, color: 'rgba(255,255,255,0.65)', padding: '6px 8px', whiteSpace: 'nowrap' };
  const V = { fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', padding: '6px 8px', textAlign: 'right' };
  return (
    <div>
      {dev.meters.map((m) => {
        const pa = f(m.u[0] * m.i[0]);
        const pb = f(m.u[1] * m.i[1]);
        const pc = f(m.u[2] * m.i[2]);
        const cumFwd = m.ep;
        const cumRev = f(m.ep * 0.57, 2);
        const dayFwd = f(m.ep * 0.008, 2);
        const dayRev = f(dayFwd * 0.28, 2);
        const energyRows = [
          { key: `${m.id}-cuF`, item: '累计正向总电能', total: f(cumFwd, 2), sharp: f(cumFwd * SLOT.sharp, 2), peak: f(cumFwd * SLOT.peak, 2), flat: f(cumFwd * SLOT.flat, 2), valley: f(cumFwd * SLOT.valley, 2), deep: f(cumFwd * SLOT.deep, 2) },
          { key: `${m.id}-cuR`, item: '累计反向总电能', total: cumRev, sharp: f(cumRev * SLOT.sharp, 2), peak: f(cumRev * SLOT.peak, 2), flat: f(cumRev * SLOT.flat, 2), valley: f(cumRev * SLOT.valley, 2), deep: f(cumRev * SLOT.deep, 2) },
          { key: `${m.id}-dyF`, item: '日正向总电能', total: dayFwd, sharp: f(dayFwd * SLOT.sharp, 2), peak: f(dayFwd * SLOT.peak, 2), flat: f(dayFwd * SLOT.flat, 2), valley: f(dayFwd * SLOT.valley, 2), deep: f(dayFwd * SLOT.deep, 2) },
          { key: `${m.id}-dyR`, item: '日反向总电能', total: dayRev, sharp: f(dayRev * SLOT.sharp, 2), peak: f(dayRev * SLOT.peak, 2), flat: f(dayRev * SLOT.flat, 2), valley: f(dayRev * SLOT.valley, 2), deep: f(dayRev * SLOT.deep, 2) },
        ];
        const gridRows = [
          { k: '有功功率 (kW)', a: pa, b: pb, c: pc, t: m.p },
          { k: '无功功率 (kVar)', a: '0.0', b: '0.0', c: '0.0', t: m.q },
          { k: '视在功率 (kVA)', a: pa, b: pb, c: pc, t: f(Math.hypot(m.p, m.q)) },
          { k: '功率因数', a: m.pf, b: m.pf, c: m.pf, t: m.pf },
          { k: '电压 (V)', a: m.u[0], b: m.u[1], c: m.u[2] },
          { k: '电流 (A)', a: m.i[0], b: m.i[1], c: m.i[2] },
        ];
        return (
          <div key={m.id} style={{ marginBottom: 20, border: '1px solid rgba(0,180,216,0.18)', borderRadius: 10, padding: 16, background: 'rgba(10,23,44,0.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{m.name}</span>
              <Tag color={m.state === '正常' ? 'green' : 'red'}>{m.state}</Tag>
              <Tag color="blue" style={{ marginRight: 0 }}>{m.wiring}</Tag>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>数据更新时间:{update}</span>
            </div>
            {/* 数据栏：A相/B相/C相/总 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px 12px', marginBottom: 16 }}>
              <div style={H}>参数</div><div style={H}>A相</div><div style={H}>B相</div><div style={H}>C相</div><div style={H}>总</div>
              {gridRows.map((r) => (
                <React.Fragment key={r.k}>
                  <div style={K}>{r.k}</div>
                  <div style={V}>{r.a}</div>
                  <div style={V}>{r.b}</div>
                  <div style={V}>{r.c}</div>
                  <div style={{ ...V, color: '#00d4ff', fontWeight: 600 }}>{r.t ?? '—'}</div>
                </React.Fragment>
              ))}
            </div>
            {/* 电能统计表格 */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: 8 }}>电能统计<small style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.45)', marginLeft: 6 }}>单位 kWh</small></div>
            <Table
              size="small" pagination={false} rowKey="key" dataSource={energyRows}
              columns={[
                { title: '类别', dataIndex: 'item' },
                { title: '总', dataIndex: 'total' },
                { title: '尖', dataIndex: 'sharp' },
                { title: '峰', dataIndex: 'peak' },
                { title: '平', dataIndex: 'flat' },
                { title: '谷', dataIndex: 'valley' },
                { title: '深谷', dataIndex: 'deep' },
              ]}
            />
          </div>
        );
      })}
    </div>
  );
};

const HvacView = ({ dev }) => (
  <div>
    <div className="dev-status-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
      {dev.hvac.map((a) => (
        <div className={`dev-card${a.alarm ? ' fault' : ''}`} key={a.id}>
          <div className="dev-card-head">
            <span className="dev-name">{a.name}</span>
            {statusDot(a.state)}
          </div>
          {kvRows([
            ['工作模式', <Tag key="mode" color="geekblue">{a.mode}</Tag>],
            ['设定温度', `${a.setTemp} ℃`],
            ['回风温度', `${a.returnTemp} ℃`],
            ['出风温度', `${a.supplyTemp} ℃`],
            ['环境湿度', `${a.humidity} %RH`],
            ['风机', a.fan],
            ['压缩机', a.compressor],
          ])}
          {a.alarm ? <div className="peak-note" style={{ color: '#ff5a7f' }}>告警：{a.alarm}</div> : null}
        </div>
      ))}
    </div>
    <div className="peak-toolbar" style={{ marginTop: 16 }}>
      <span className="peak-toolbar-label">支持的工作模式（{HVAC_MODES.length} 种）</span>
      <Space size={6} wrap>
        {HVAC_MODES.map((m) => (
          <Tag key={m} color={dev.hvac.some((a) => a.mode === m) ? 'green' : 'default'}>{m}</Tag>
        ))}
      </Space>
    </div>
    <div className="peak-note">绿色为当前站点空调正在使用的模式；空调开关机与参数配置在「控制中心 · 设备控制」。</div>
  </div>
);

const LiquidView = ({ dev }) => (
  <div className="dev-status-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
    <div className="dev-card">
      <div className="dev-card-head"><span className="dev-name">{dev.liquid.name}</span>{statusDot(dev.liquid.state)}</div>
      {kvRows([
        ['运行状态', dev.liquid.running ? '运行' : '停机'],
        ['工作模式', dev.liquid.mode],
        ['设定温度', `${dev.liquid.setTemp} ℃`],
        ['出水温度', `${dev.liquid.supplyTemp} ℃`],
        ['回水温度', `${dev.liquid.returnTemp} ℃`],
        ['水泵', dev.liquid.pump],
        ['风机', dev.liquid.fan],
        ['系统压力', `${dev.liquid.pressure} MPa`],
      ])}
    </div>
  </div>
);

// ---------------- 消防安全（独有） ----------------
const FireView = ({ dev }) => {
  const [measure, setMeasure] = useState(null);
  const [writeVal, setWriteVal] = useState(null);
  const sensors = dev.fire.filter((f) => f.cat !== '消防主机');
  return (
    <div>
      <div className="panel-title" style={{ marginBottom: 10 }}>IO 控制器<small>DI / DO 端口状态（端口名称可在系统管理 · 设备管理修改）</small></div>
      <div className="peak-io-grid" style={{ marginBottom: 16 }}>
        {FIRE_IO.map((io) => (
          <div className="dev-card" key={io.id}>
            <div className="dev-card-head"><span className="dev-name">{io.name}</span>{statusDot(io.state)}</div>
            <div className="peak-io-group">
              <div className="peak-io-group-title">数字输入 DI</div>
              {io.di.map((d) => (
                <div className="peak-io-row" key={d.key}>
                  <span className="peak-io-k">{d.key} · {d.name}</span>
                  <span className={`peak-io-v ${d.value === '闭合' ? 'on' : 'off'}`}>{d.value}</span>
                </div>
              ))}
            </div>
            <div className="peak-io-group">
              <div className="peak-io-group-title">数字输出 DO</div>
              {io.do.map((d) => (
                <div className="peak-io-row" key={d.key}>
                  <span className="peak-io-k">{d.key} · {d.name}</span>
                  <span className={`peak-io-v ${d.value === '闭合' ? 'on' : 'off'}`}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="panel-title" style={{ marginBottom: 10 }}>传感器<small>温湿度 / 水浸 / 烟感等</small></div>
      <div className="dev-status-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 16 }}>
        {sensors.map((s) => (
          <div className={`dev-card${s.state !== '正常' ? ' fault' : ''}`} key={s.id}>
            <div className="dev-card-head"><span className="dev-name">{s.name}</span>{statusDot(s.state)}</div>
            {kvRows(s.rows)}
          </div>
        ))}
      </div>

      <div className="panel-title" style={{ marginBottom: 10 }}>测控装置<small>参数由配置文件手动配置 · 可对测控点位单次写入</small></div>
      <div className="dev-status-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        {FIRE_MEASURE.map((m) => (
          <div className="dev-card" key={m.id}>
            <div className="dev-card-head">
              <span className="dev-name">{m.name}</span>
              <Space>
                {statusDot(m.state)}
                <Button size="small" className="panel-title-btn" onClick={() => { setMeasure(m); setWriteVal(null); }}>配置</Button>
              </Space>
            </div>
            {kvRows(m.rows.map((r) => [r.name, r.value]))}
          </div>
        ))}
      </div>

      <Modal
        title="测控装置 · 单次写入"
        open={!!measure}
        okText="确认写入"
        cancelText="取消"
        onCancel={() => setMeasure(null)}
        onOk={() => {
          if (writeVal === null) { message.warning('请输入写入值'); return; }
          message.success(`${measure.name} 已下发单次写入（演示）`);
          setMeasure(null);
        }}
        destroyOnHidden
      >
        {measure ? (
          <div className="peak-note" style={{ marginBottom: 10 }}>测控装置参数由配置文件定义，此处可对选中的测控点位执行单次写入。</div>
        ) : null}
        <div className="peak-form-row">
          <span className="peak-form-label">写入点位</span>
          <Select style={{ width: 220 }} placeholder="选择点位"
            options={(measure ? measure.rows : []).map((r) => ({ value: r.name, label: `${r.name}（当前 ${r.value}）` }))} />
        </div>
        <div className="peak-form-row">
          <span className="peak-form-label">写入值</span>
          <InputNumber value={writeVal} onChange={setWriteVal} style={{ width: 220 }} placeholder="输入数值" />
        </div>
      </Modal>
    </div>
  );
};

// ---------------- DCDC / UPS（独有） ----------------
// DCDC 对齐文档：状态摘要（运行/告警）+ 高压侧参数 / 低压侧参数 / 累计·环境参数 三栏
const DcdcView = () => (
  <div>
    {DCDC_DEVICES.map((d) => (
      <div className={`dev-card${d.alarm !== '正常' || !d.running ? ' fault' : ''}`} key={d.id} style={{ marginBottom: 16 }}>
        <div className="dev-card-head"><span className="dev-name">{d.name}<Tag color="purple" style={{ marginLeft: 8 }}>DCDC</Tag></span><span className="dev-status"><span className={`dot ${d.running ? 'online' : 'offline'}`} />{d.running ? '运行' : '停机'}</span></div>
        <div className="dev-kv" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 12 }}>
          {kvRows([
            ['运行状态', d.running ? '运行' : '停机'],
            ['告警状态', d.alarm],
          ])}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 18px' }}>
          {[
            ['高压侧参数', [
              ['高压侧总电压', `${d.hvVoltage} V`], ['高压侧电流', `${d.hvCurrent} A`],
              ['高压侧直流功率', `${d.hvPower} kW`], ['高压侧正极母线电压', `${d.hvPosBus} V`],
              ['高压侧负极母线电压', `${d.hvNegBus} V`], ['高压侧正负母线压差', `${d.hvDiff} V`],
            ]],
            ['低压侧参数', [
              ['低压侧总电压', `${d.lvVoltage} V`], ['低压侧电流', `${d.lvCurrent} A`],
              ['低压侧直流功率', `${d.lvPower} kW`], ['低压侧正极母线电压', `${d.lvPosBus} V`],
              ['低压侧负极母线电压', `${d.lvNegBus} V`], ['低压侧正负母线压差', `${d.lvDiff} V`],
            ]],
            ['累计/环境参数', [
              ['总充电量', `${d.totalCharge} kWh`], ['总放电量', `${d.totalDischarge} kWh`],
              ['散热器温度', `${d.radiatorTemp} ℃`], ['环境温度', `${d.envTemp} ℃`],
            ]],
          ].map(([title, rows]) => (
            <div key={title}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>{title}</div>
              {rows.map(([k, v]) => (
                <div className="dev-kv-row" key={k}><span className="dev-kv-k">{k}</span><span className="dev-kv-v">{v}</span></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// UPS 对齐文档：输入电压 / 输入频率 / 环境温度 / 电池电压 / 输出电压 / 输出电流 / 负载百分比
const UpsView = ({ dev }) => (
  <div>
    {dev.others.filter((o) => /^UPS/.test(o.id)).map((o) => (
      <div className="dev-card" key={o.id}>
        <div className="dev-card-head"><span className="dev-name">{o.name}<Tag color="blue" style={{ marginLeft: 8 }}>UPS</Tag></span><span className="dev-status"><span className={`dot ${o.running ? 'online' : 'offline'}`} />{o.state}</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 12px' }}>
          {[
            ['输入电压', `${o.voltage} V`],
            ['输入频率', '50 Hz'],
            ['环境温度', '28 ℃'],
            ['电池电压', '48 V'],
            ['输出电压', `${o.voltage} V`],
            ['输出电流', `${(o.load / 8).toFixed(1)} A`],
            ['负载百分比', `${o.load} %`],
          ].map(([k, v]) => (
            <div key={k} style={{ padding: '7px 10px', background: 'rgba(10,23,44,0.5)', border: '1px solid rgba(0,180,216,0.12)', borderRadius: 6, display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span className="dev-kv-k" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{k}</span>
              <span className="dev-kv-v" style={{ fontSize: 13, fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ---------------- 电池极柱温度（3.14 · 削峰填谷独有 · 文档仅菜单名，内容自行补充） ----------------
const PoleTempView = ({ dev, station }) => {
  // 每个电池堆极柱 ± / 正负极温度（按堆展示：正极柱 / 负极柱 / 环境温度）
  const rows = dev.racks.map((r, i) => {
    const base = r.temp;
    const posTerminal = +(base + 3.2 + Math.sin(i) * 0.8).toFixed(1);
    const negTerminal = +(base + 2.6 + Math.cos(i * 1.3) * 0.9).toFixed(1);
    const alarm = posTerminal > 55 || negTerminal > 55 || base > 50;
    return {
      key: r.id, name: r.name,
      pos: posTerminal, neg: negTerminal, env: base,
      diff: +Math.abs(posTerminal - negTerminal).toFixed(1),
      alarm,
    };
  });
  return (
    <div>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi-card"><div className="kpi-label">最高极柱温度</div><div className="kpi-value">{Math.max(...rows.map((r) => Math.max(r.pos, r.neg)))}<span className="unit">℃</span></div><div className="kpi-foot">全部电池堆正/负极柱最高</div></div>
        <div className="kpi-card"><div className="kpi-label">平均极柱温度</div><div className="kpi-value">{(rows.reduce((s, r) => s + (r.pos + r.neg) / 2, 0) / rows.length).toFixed(1)}<span className="unit">℃</span></div><div className="kpi-foot">全部电池堆正/负极柱平均</div></div>
        <div className="kpi-card"><div className="kpi-label">告警堆数</div><div className="kpi-value">{rows.filter((r) => r.alarm).length}<span className="unit">堆</span></div><div className="kpi-foot">极柱温度偏高（&gt;55℃）</div></div>
      </div>
      <Table
        size="small" pagination={false} rowKey="key" dataSource={rows}
        columns={[
          { title: '电池堆', dataIndex: 'name' },
          { title: '正极柱 (℃)', dataIndex: 'pos', render: (v, r) => <span style={r.pos > 55 ? { color: '#ff5a7f' } : undefined}>{v}</span> },
          { title: '负极柱 (℃)', dataIndex: 'neg', render: (v, r) => <span style={r.neg > 55 ? { color: '#ff5a7f' } : undefined}>{v}</span> },
          { title: '环境温度 (℃)', dataIndex: 'env' },
          { title: '正负温差 (℃)', dataIndex: 'diff' },
          { title: '状态', dataIndex: 'alarm', render: (v) => (v ? <Tag color="red">偏高</Tag> : <Tag color="green">正常</Tag>) },
        ]}
      />
      <div className="peak-note">电池极柱温度反映连接端子与导流排的发热情况。一般不超过 55℃，若某堆正/负极柱温度明显偏高，提示可能存在极柱接触不良或内阻增大。</div>
    </div>
  );
};

// ---------------- 光伏逆变器（条件展示） ----------------
const PvView = ({ dev }) => (
  <div>
    <div className="dev-status-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
      { (dev.pv.length ? dev.pv : PK_PV_FALLBACK).map((p) => (
        <div className="dev-card" key={p.id}>
          <div className="dev-card-head"><span className="dev-name">{p.name}</span><span className="dev-status"><span className={`dot ${p.running ? 'online' : 'offline'}`} />{p.state}</span></div>
          {kvRows([
            ['直流功率', `${p.dcPower} kW`],
            ['交流功率', `${p.acPower} kW`],
            ['今日发电量', `${p.todayEnergy} kWh`],
            ['MPPT 路数', p.mppt],
            ['直流电压', `${p.dcVoltage} V`],
            ['转换效率', `${p.efficiency} %`],
          ])}
        </div>
      ))}
    </div>
    <div className="peak-note">光伏逆变器开机 / 关机在「控制中心 · 设备控制」。</div>
  </div>
);

const PeakDevice = ({ station }) => {
  const dev = getDevices(station.id);
  // 对齐需求文档 p4 设备监控截图的左侧树导航顺序（图1：…消防安全 → 光伏逆变器 → 液冷机组 → DCDC → UPS）
  const items = [
    { key: 'rt', label: '实时运行', children: <RealtimeView dev={dev} station={station} /> },
    { key: 'pcs', label: 'PCS', children: <PcsView dev={dev} /> },
    { key: 'bms', label: 'BMS 总览', children: <BmsView dev={dev} /> },
    { key: 'cluster', label: 'BMS 电池簇', children: <ClusterView dev={dev} /> },
    { key: 'poletemp', label: '电池极柱温度', children: <PoleTempView dev={dev} station={station} /> },
    { key: 'cell', label: '单体电池', children: <CellView dev={dev} /> },
    { key: 'meter', label: '电表', children: <MeterView dev={dev} /> },
    { key: 'hvac', label: '空调', children: <HvacView dev={dev} /> },
    { key: 'fire', label: '消防安全', children: <FireView dev={dev} /> },
    { key: 'liquid', label: '液冷机组', children: <LiquidView dev={dev} /> },
    { key: 'dcdc', label: 'DCDC', children: <DcdcView dev={dev} /> },
    { key: 'ups', label: 'UPS', children: <UpsView dev={dev} /> },
  ];
  // 图1 左侧导航含「光伏逆变器」，削峰填谷设备监控页一律展示（数据不足时用 PK_PV_FALLBACK）
  items.splice(9, 0, { key: 'pv', label: '光伏逆变器', children: <PvView dev={dev} /> });

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
};

export default PeakDevice;
